import React from 'react';
import { Text } from '@lmn/design-system';
import { css } from 'styled-components';
import RoomMainAmenities, { RoomMainAmenity, Separator } from '@/app/components/hotel-detail/rooms/RoomMainAmenities';
import { RoomSimpleAmenities } from '@/app/components/hotel-detail/rooms/RoomSimpleAmenities';
import ImageSlider from '@/app/components/hotel-detail/ImageSlider';
import { useLoadableImages } from '@/app/components/hotel-detail/hooks/useLoadableImages';
import customCss from '@/app/components/hotel-detail/css/customCss';
import { usePageWrapperData } from '@/app/common/pageWrapperProvider';
import { EvoRoomAvailabilityAlertLabel } from '@/app/components/hotel-detail/evo/rooms/EvoRoomAvailabilityAlertLabel';
import { RoomsIncludedInPrice } from './RoomPanelCommon';
import { RoomAvailabilityAlertLabel } from './RoomAvailabilityAlertLabel';
import { useTranslation } from '../../translations/Translation';
import { getMainAmenities } from './roomCommon';
import { RoomBedOptions } from './RoomBedOptions';

export type DescriptionType = {
  type: string;
  text: string;
};

type DetailsProps = {
  simpleAmenities: { type: string; description: string; highlight: boolean }[];
  descriptions: readonly DescriptionType[];
} & React.HTMLAttributes<HTMLElement>;

function Details(props: DetailsProps) {
  const { simpleAmenities, descriptions, ...rest } = props;

  const description = getRoomGeneralDescription(descriptions);

  return (
    <div
      css={css`
        color: ${({ theme }) => theme.commons.colors.gray400};
        display: flex;
        flex-wrap: wrap;
        row-gap: 16px;
      `}
      {...rest}
    >
      {description && (
        <Text
          variant="BodySmall"
          css={css`
            width: 100%;
          `}
        >
          {description}
        </Text>
      )}
      {simpleAmenities.length > 0 && (
        <RoomSimpleAmenities
          css={css`
            margin: 0;
            width: 100%;
          `}
          amenities={simpleAmenities}
        />
      )}
    </div>
  );
}

function getRoomGeneralDescription(descriptionsArray: readonly DescriptionType[]): string | boolean {
  const generalDescription = descriptionsArray.find((description) => description?.type === 'GENERAL');

  if (generalDescription) {
    const text = generalDescription?.text?.trim();
    return text.length > 5 ? text : false;
  }
  return false;
}

/*
 * Temporal fix to avoid showing room names with a dot at the end.
 * This should be fixed at supply level
 */
function removeDotsFromEndOfString(str: string) {
  let newStr = str.trim();
  while (newStr[newStr.length - 1] === '.') newStr = newStr.slice(0, -1);
  return newStr;
}

type RoomPanelModalContentProps = {
  room: RoomType;
};

export function RoomPanelModalContent(props: RoomPanelModalContentProps) {
  const { room } = props;
  const { t } = useTranslation();

  const roomsLeft = room.configurations?.[0]?.roomsLeft;

  const mainAmenities = getMainAmenities(t, room);
  const simpleAmenities = room.amenities.filter((amenity) => amenity.type === 'GENERAL');
  // MPH-1321: photos that fail to load leave the gallery and its counter.
  const { loadableImages, onImageFallback } = useLoadableImages(room.images);

  return (
    <div
      css={`
        display: flex;
        flex-direction: column;
        padding: 16px;
        row-gap: 24px;

        @media ${customCss.breakpoints.lg} {
          padding: 24px;
        }
      `}
    >
      {loadableImages.length ? (
        <ImageSlider
          images={loadableImages}
          onImageFallback={onImageFallback}
          imageAltName={room.name}
          showCounterIcon={false}
          css={css`
            height: 360px;

            border-radius: 8px;
            overflow: hidden;

            @media ${customCss.breakpoints.xl} {
              border-radius: 16px;
            }

            & .slider-overlay-arrow-left,
            & .slider-overlay-arrow-right {
              display: flex;

              background: rgba(255, 255, 255, 1);
              border-radius: 32px;
              width: 32px;
              height: 32px;
              align-items: center;

              &:hover {
                background: rgba(255, 255, 255, 0.9);
              }

              & > [data-library-icon] {
                color: ${(p) => p.theme.commons.colors.gray800};
                width: 16px;
                height: 16px;
              }
            }

            & .slider-overlay-arrow-left {
              left: 16px;
            }

            & .slider-overlay-arrow-right {
              right: 16px;
            }
          `}
        />
      ) : undefined}
      <div
        css={`
          display: flex;
          flex-direction: column;
          row-gap: 24px;
        `}
      >
        <RoomTitle name={room.name} roomsIncludedInPrice={room.roomsIncludedInPrice} roomsLeft={roomsLeft} />
        <div
          css={`
            display: flex;
            flex-direction: column;
            row-gap: 16px;
          `}
        >
          {room.bedOptions && room.bedOptions.length ? (
            <>
              {/* {shouldShowRoomAvailabilityAlertLabel(roomsLeft) && <Separator />} */}
              <RoomBedOptions bedOptions={room.bedOptions} iconSize={20} />
              {/* {mainAmenities.length > 0 && <Separator />} */}
            </>
          ) : undefined}

          {mainAmenities.length > 0 && (
            <RoomMainAmenities
              amenities={mainAmenities}
              iconColor="iconPrimary"
              iconSize={20}
              css={`
                display: flex;
                flex-wrap: wrap;
                width: 100%;
                column-gap: 16px;
                row-gap: 8px;

                ${RoomMainAmenity} {
                  align-items: center;
                }
              `}
            />
          )}
          <Separator />
          <Details simpleAmenities={simpleAmenities} descriptions={room.descriptions} />
        </div>
      </div>
    </div>
  );
}

type RoomTitleProps = {
  name: string;
  roomsIncludedInPrice: number;
  roomsLeft: number;
};

function RoomTitle(props: RoomTitleProps) {
  const { name, roomsIncludedInPrice, roomsLeft } = props;
  const { localization } = usePageWrapperData();
  const evolutionEnabled = localization?.configValuesApp?.evolutionEnabled;

  return (
    <div
      css={css`
        ${
          evolutionEnabled &&
          css`
            display: flex;
            flex-direction: column;
            align-items: flex-start;
            gap: ${({ theme }) => theme.kallisto.space.space100};
          `
        }
      `}
    >
      <div
        css={`
          display: flex;
          gap: 4px 8px;
          align-items: center;
        `}
      >
        {roomsIncludedInPrice > 1 && <RoomsIncludedInPrice roomsIncludedInPrice={roomsIncludedInPrice} />}
        <Text variant="Title4" forceTag="h3">
          {removeDotsFromEndOfString(name)}
        </Text>
      </div>
      {evolutionEnabled ? (
        <EvoRoomAvailabilityAlertLabel roomsLeft={roomsLeft} />
      ) : (
        <RoomAvailabilityAlertLabel roomsLeft={roomsLeft} iconSize={20} />
      )}
    </div>
  );
}
